/**
 * Command palette (cmd/ctrl + k).
 *
 * Linear-style. Opens on cmd+k anywhere. Supports:
 *   - Fuzzy search jobs by code, title, or client name.
 *   - Navigation targets (Dashboard, Inbox, Settings).
 *   - Quick actions (New brief on current job, sync Gmail, etc).
 *
 * Pure keyboard: arrow keys navigate, Enter selects, Esc closes,
 * type to filter. Results refresh debounced as the user types.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { jobs as jobsApi, gmail } from '@/lib/api';
import type { Job } from '@/lib/types';
import { classNames } from '@/lib/format';
import { useToast } from './Toast';
import { isPictureUpPage } from '@/lib/pu-class';

type CommandItem = {
  id: string;
  label: string;
  hint?: string;
  group: 'Navigation' | 'Jobs' | 'Actions';
  /** Optional initials for the avatar tile (used by job rows). */
  initials?: string;
  perform: () => void | Promise<void>;
};

type Context = {
  open: () => void;
  close: () => void;
};

const PaletteContext = createContext<Context | null>(null);

export function CommandPaletteProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);

  // Global cmd/ctrl + k toggle. Also Esc to close (handled inside dialog).
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;
      // The palette is the CRM's (Kilogram jobs, Kilogram look). PictureUp
      // pages, share links included, never open it.
      if (isPictureUpPage(window.location.pathname)) return;
      if (mod && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  return (
    <PaletteContext.Provider value={{ open: () => setOpen(true), close: () => setOpen(false) }}>
      {children}
      {open && <Palette onClose={() => setOpen(false)} />}
    </PaletteContext.Provider>
  );
}

export function useCommandPalette(): Context {
  const ctx = useContext(PaletteContext);
  if (!ctx) return { open: () => {}, close: () => {} };
  return ctx;
}

// ---------------------------------------------------------------------
// The palette itself
// ---------------------------------------------------------------------

function Palette({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const [query, setQuery] = useState('');
  const [jobs, setJobs] = useState<Job[] | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Lazy-load jobs once when palette opens. Cheap (< 200 rows in practice).
  useEffect(() => {
    let cancelled = false;
    jobsApi
      .list()
      .then((list) => {
        if (!cancelled) setJobs(list);
      })
      .catch(() => {
        if (!cancelled) setJobs([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Focus the input on mount.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const items: CommandItem[] = useMemo(() => {
    const nav: CommandItem[] = [
      {
        id: 'nav-dashboard',
        label: 'Go to Dashboard',
        hint: 'G then D',
        group: 'Navigation',
        perform: () => navigate('/'),
      },
      {
        id: 'nav-timelines',
        label: 'Go to Timelines',
        group: 'Navigation',
        perform: () => navigate('/timelines'),
      },
      {
        id: 'nav-clients',
        label: 'Go to Clients',
        group: 'Navigation',
        perform: () => navigate('/clients'),
      },
      {
        id: 'nav-inbox',
        label: 'Go to Inbox',
        hint: 'G then I',
        group: 'Navigation',
        perform: () => navigate('/inbox'),
      },
      {
        id: 'nav-settings',
        label: 'Go to Settings',
        hint: 'G then S',
        group: 'Navigation',
        perform: () => navigate('/settings'),
      },
    ];

    const jobMatch = location.pathname.match(/^\/jobs\/([^/]+)/);
    const currentJobId = jobMatch ? jobMatch[1] : null;

    const actions: CommandItem[] = [
      {
        id: 'action-new-job',
        label: 'New job',
        group: 'Actions',
        perform: () => navigate('/jobs/new'),
      },
      {
        id: 'action-new-client',
        label: 'New client',
        group: 'Actions',
        perform: () => navigate('/clients/new'),
      },
      {
        id: 'action-sync-gmail',
        label: 'Sync Gmail (last 30 days)',
        group: 'Actions',
        perform: async () => {
          try {
            const res = await gmail.sync({});
            toast.success(`Synced ${res.ingestedMessages} new messages`);
          } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Sync failed');
          }
        },
      },
    ];
    if (currentJobId) {
      actions.push({
        id: 'action-new-brief',
        label: 'New brief on this job',
        hint: 'N',
        group: 'Actions',
        perform: () => navigate(`/jobs/${currentJobId}/briefs/new`),
      });
      actions.push({
        id: 'action-feedback',
        label: 'Open feedback triage on this job',
        group: 'Actions',
        perform: () => navigate(`/jobs/${currentJobId}/feedback`),
      });
    }

    const jobItems: CommandItem[] = (jobs ?? []).map((j) => ({
      id: `job-${j.id}`,
      label: `${j.code}  ${j.title}`,
      hint: j.client?.name ?? '',
      group: 'Jobs',
      initials: (j.client?.name ?? j.title).slice(0, 2).toUpperCase(),
      perform: () => navigate(`/jobs/${j.id}`),
    }));

    return [...nav, ...actions, ...jobItems];
  }, [jobs, location.pathname, navigate, toast]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => {
      const hay = `${it.label} ${it.hint ?? ''} ${it.group}`.toLowerCase();
      // Tiny fuzzy: all query characters must appear in order.
      let i = 0;
      for (const ch of q) {
        const idx = hay.indexOf(ch, i);
        if (idx < 0) return false;
        i = idx + 1;
      }
      return true;
    });
  }, [items, query]);

  // Reset selection on query change.
  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  const selectItem = useCallback(
    async (idx: number) => {
      const it = filtered[idx];
      if (!it) return;
      onClose();
      await it.perform();
    },
    [filtered, onClose],
  );

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      void selectItem(activeIndex);
    }
  };

  // Group consecutive items by group header for visual clarity.
  const grouped: Array<{ group: CommandItem['group']; items: CommandItem[] }> = [];
  for (const it of filtered) {
    const last = grouped[grouped.length - 1];
    if (last && last.group === it.group) last.items.push(it);
    else grouped.push({ group: it.group, items: [it] });
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
      className="fixed inset-0 z-[70] flex items-start justify-center p-4 sm:pt-[12vh]"
    >
      <div
        className="fixed inset-0 bg-kg-black/40 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="kg-pane relative w-full max-w-xl flex flex-col max-h-[70vh] overflow-hidden text-kg-black dark:text-kg-grey-100">
        <div className="px-4 py-3 flex items-center gap-3">
          <span className="kg-kbd shrink-0">⌘K</span>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search jobs, navigate, run an action…"
            aria-label="Command input"
            className="flex-1 bg-transparent border-0 outline-none text-sm placeholder:text-kg-grey-500 dark:text-kg-grey-400"
          />
          <button
            onClick={onClose}
            className="kg-btn-ghost"
            aria-label="Close command palette"
          >
            esc
          </button>
        </div>
        <div className="flex-1 overflow-y-auto py-2">
          {filtered.length === 0 ? (
            <p className="px-4 py-6 text-sm text-kg-grey-600 dark:text-kg-grey-300">No matches.</p>
          ) : (
            grouped.map((g) => {
              const offset = filtered.indexOf(g.items[0]!);
              return (
                <div key={g.group} className="mb-2">
                  <p className="px-4 py-1 text-xs uppercase tracking-widest text-kg-grey-500 dark:text-kg-grey-400">
                    {g.group}
                  </p>
                  {g.items.map((it, i) => {
                    const idx = offset + i;
                    const active = idx === activeIndex;
                    return (
                      <button
                        key={it.id}
                        onMouseEnter={() => setActiveIndex(idx)}
                        onClick={() => void selectItem(idx)}
                        className={classNames(
                          'w-full text-left flex items-center gap-3 px-4 py-2 text-sm',
                          active ? 'bg-kg-grey-100 text-kg-black' : 'text-kg-grey-800 dark:text-kg-grey-100',
                        )}
                      >
                        {it.initials && (
                          <span className="inline-flex w-6 h-6 items-center justify-center text-xs font-medium bg-kg-grey-100 text-kg-grey-700 dark:text-kg-grey-200 rounded">
                            {it.initials}
                          </span>
                        )}
                        <span className="flex-1 truncate">{it.label}</span>
                        {it.hint && (
                          <span className="text-xs text-kg-grey-500 dark:text-kg-grey-400 shrink-0">
                            {it.hint}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              );
            })
          )}
        </div>
        <div className="px-4 py-2 bg-white/40 dark:bg-white/[0.05] flex items-center justify-between text-xs text-kg-grey-500 dark:text-kg-grey-400">
          <span className="flex items-center gap-2">
            <span className="kg-kbd">↑</span>
            <span className="kg-kbd">↓</span>
            <span>navigate</span>
            <span className="kg-kbd">↵</span>
            <span>select</span>
          </span>
          <span className="flex items-center gap-2">
            <span className="kg-kbd">esc</span>
            <span>dismiss</span>
          </span>
        </div>
      </div>
    </div>
  );
}
